import { Overlay } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS,
  MatNativeDateModule,
} from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { WidgetCacheInterceptor } from '@shared/api/widget-cache.service';
import { DirectivesModule } from '@shared/directives/directives.module';
import { SeoService } from '@shared/helpers/seo.service';
import { PipesModule } from '@shared/pipes/pipes.module';
import { SettingsState } from '@shared/states/settings.state';
import { ThemeState } from '@shared/states/theme.state';
import { TranslateService } from '@shared/translates/translate.service';
import { TranslatesModule } from '@shared/translates/translates.module';
import { SpinnerModule } from '@ui/spinner/spinner.module';
import { GlobalErrorHandler } from 'app/config/error-handler';
import { GlobalInjector } from 'app/config/global-injector';
import { HttpErrorInterceptor } from 'app/interceptors/http-error.interceptor';
import { TokenInterceptor } from 'app/interceptors/token.interceptor';
import { LayoutModule } from 'app/layout/layout.module';
import { NotificationsModule } from 'app/notifications/notifications.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  DEFAULT_DATE_FORMATS,
  GLOBAL_DATE_ADAPTER,
  GlobalDateAdapter,
} from './config/global-date-adapter';
import { GLOBAL_DATE_TIME_ADAPTER, GlobalDateTimeAdapter } from './config/global-date-time-adapter';
import {
  globalRippleConfig,
  matButtonToggleDefaultOptions,
  matSlideToggleDefaultOptions,
} from './config/mat-config';
import { initApp } from './initializers/init-app';
import { initLanguage } from './initializers/init-language';
import { initTheme } from './initializers/init-theme';

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    CommonModule,
    TranslatesModule,
    ScrollingModule,
    MatNativeDateModule,
    RouterModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    DirectivesModule,
    LayoutModule,
    NotificationsModule,
    PipesModule,
    SpinnerModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      multi: true,
      deps: [SettingsState],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initLanguage,
      multi: true,
      deps: [TranslateService, SeoService],
    },
    { provide: APP_INITIALIZER, useFactory: initTheme, multi: true, deps: [ThemeState] },
    {
      provide: DateAdapter,
      useExisting: GLOBAL_DATE_ADAPTER,
    },
    { provide: GLOBAL_DATE_ADAPTER, useClass: GlobalDateAdapter },
    {
      provide: GLOBAL_DATE_TIME_ADAPTER,
      useFactory: (locale: string) => new GlobalDateTimeAdapter(locale),
      deps: [MAT_DATE_LOCALE],
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (translateService: TranslateService) => {
        return translateService.currentLanguage.locale;
      },
      deps: [TranslateService],
    },
    { provide: MAT_DATE_FORMATS, useValue: DEFAULT_DATE_FORMATS },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    // Last in the chain, so it sits closest to the backend: a cache hit skips the network and
    // nothing else. Only requests that opted in via `BaseHttpService.cachedRequest()` go through it.
    { provide: HTTP_INTERCEPTORS, useClass: WidgetCacheInterceptor, multi: true },
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
    { provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, useValue: matButtonToggleDefaultOptions },
    { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: matSlideToggleDefaultOptions },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useFactory: (overlay: Overlay) => ({
        scrollStrategy: overlay.scrollStrategies.block(),
      }),
      deps: [Overlay],
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(injector: Injector) {
    GlobalInjector._injector = injector;
  }
}
